import Keycloak from 'keycloak-js'
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { setKeycloakInstance } from './keycloakInstance'

/**
 * Runtime UI configuration fetched from the backend.
 */
interface UiConfig {
    keycloakUrl: string
    keycloakRealm: string
    keycloakClientId: string
}

/**
 * Fetch runtime Keycloak configuration from the backend.
 * Falls back to Vite env vars / dev defaults if the endpoint is unreachable
 * (e.g., running `npm run dev` standalone without the Quarkus backend).
 */
async function fetchUiConfig(): Promise<UiConfig> {
    try {
        const resp = await fetch('/api/v1/config')
        if (resp.ok) {
            return await resp.json()
        }
    } catch {
        // Backend not reachable — use build-time / dev defaults
    }
    return {
        keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180',
        keycloakRealm: import.meta.env.VITE_KEYCLOAK_REALM || 'variphy-support',
        keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'admin-portal',
    }
}

/**
 * Auth context type.
 */
interface AuthContextType {
    isAuthenticated: boolean
    isLoading: boolean
    token: string | undefined
    userId: string | undefined
    userEmail: string | undefined
    userName: string | undefined
    roles: string[]
    login: () => void
    logout: () => void
    hasRole: (role: string) => boolean
    isAdmin: () => boolean
    isSupportEngineer: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within a KeycloakProvider')
    }
    return context
}

interface KeycloakProviderProps {
    children: ReactNode
}

/**
 * Keycloak authentication provider.
 * Fetches runtime config from the backend, then initialises keycloak-js.
 */
export function KeycloakProvider({ children }: KeycloakProviderProps) {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [roles, setRoles] = useState<string[]>([])
    const keycloakRef = useRef<Keycloak | null>(null)

    useEffect(() => {
        let refreshTimer: ReturnType<typeof setInterval> | undefined

        const extractRoles = (kc: Keycloak): string[] =>
            kc.tokenParsed?.realm_access?.roles || []

        const init = async () => {
            const config = await fetchUiConfig()
            const kc = new Keycloak({
                url: config.keycloakUrl,
                realm: config.keycloakRealm,
                clientId: config.keycloakClientId,
            })
            keycloakRef.current = kc
            setKeycloakInstance(kc)

            try {
                const authenticated = await kc.init({
                    onLoad: 'login-required',
                    checkLoginIframe: false,
                    pkceMethod: 'S256',
                    redirectUri: window.location.href,
                })
                console.info('Keycloak init result: authenticated =', authenticated)
                setIsAuthenticated(authenticated)
                if (authenticated) {
                    setRoles(extractRoles(kc))
                }
            } catch (error) {
                console.error('Keycloak initialization failed:', error)
                setIsAuthenticated(false)
            } finally {
                setIsLoading(false)
            }

            // Set up token refresh — sync role changes on each refresh
            refreshTimer = setInterval(() => {
                if (kc.authenticated) {
                    kc.updateToken(60)
                        .then((refreshed) => {
                            if (refreshed) {
                                setRoles(extractRoles(kc))
                            }
                        })
                        .catch(() => {
                            console.warn('Token refresh failed, logging out')
                            kc.logout()
                        })
                }
            }, 60000)
        }

        init()

        return () => {
            if (refreshTimer) clearInterval(refreshTimer)
        }
    }, [])

    const keycloak = keycloakRef.current

    const login = () => {
        keycloak?.login()
    }

    const logout = () => {
        keycloak?.logout()
    }

    const hasRole = (role: string): boolean => {
        return keycloak?.hasRealmRole(role) || keycloak?.hasResourceRole(role) || false
    }

    const isAdmin = (): boolean => hasRole('admin')

    const isSupportEngineer = (): boolean => hasRole('support_engineer')

    const value: AuthContextType = {
        isAuthenticated,
        isLoading,
        token: keycloak?.token,
        userId: keycloak?.tokenParsed?.sub,
        userEmail: keycloak?.tokenParsed?.email as string | undefined,
        userName: keycloak?.tokenParsed?.name as string | undefined,
        roles,
        login,
        logout,
        hasRole,
        isAdmin,
        isSupportEngineer,
    }

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner" />
                <p>Authenticating...</p>
            </div>
        )
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
